// The episode editor. An episode opens in the view it was last left in (Quick until switched; see EditorShell), with
// Quick | Script | Timeline tabs. This file is the full timeline view: top bar, the episode header (preview, tabs, add buttons), the scrolling timeline and an action bar for a selected clip. The playhead is fixed in
// the middle of the lanes; scrolling the lanes moves time under it. The add/edit pages open over this screen.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { decodeImages } from "../../library/images";
import { useLibrary } from "../../library/store";
import { imagesInRange } from "../../engine/timelineFrame";
import { lineById, lineAtTime, pictureAt, canPlay, displayDuration, moveLine, removeLine, removeMarker } from "../../model/timeline";
import { Page, useTimelinePlayback } from "../common";
import { EditorBar, loadView, saveView, useEditorPages, type View } from "./EditorShell";
import type { PageKind } from "./pages";
import { placeholderToFill } from "./pages/shared";
import { Heads, Lanes, PX, type Sel } from "./Lanes";
import { FullPlayer } from "./FullPlayer";
import { ScriptView } from "./ScriptView";
import { QuickView } from "../quick/QuickView";
import { EpisodeHead, Tile } from "./EpisodeHead";
import { fmtT } from "./Stage";
import { findSel, Toolbar, type Act } from "./Toolbar";
import { useTimeline, type EditorProps } from "./TimelineLoader";
import "./timeline.css";

// Picks between the three views; the choice is remembered for this episode.
export function TimelineEditor(p: EditorProps) {
  const { ep } = useTimeline();
  const [view, setView] = useState<View>(() => (ep.demo ? "quick" : loadView(ep.id))); // (a demo is always the quick screen)
  const change = (v: View) => { setView(v); saveView(ep.id, v); };
  return view === "quick" ? <QuickView {...p} view={view} onView={change} />
    : view === "script" ? <ScriptView onBack={p.onBack} view={view} onView={change} /> : <FullTimeline onBack={p.onBack} view={view} onView={change} />;
}

export function FullTimeline({ onBack, view, onView }: EditorProps & { view: View; onView: (v: View) => void }) {
  const { ep, update } = useTimeline();
  const lib = useLibrary();
  const total = displayDuration(ep);
  const playable = canPlay(ep);
  const [t, setT] = useState(0);
  const [sel, setSel] = useState<Sel | null>(null);
  const [half, setHalf] = useState(0);
  const [full, setFull] = useState(false); // playing full screen
  const scroller = useRef<HTMLDivElement>(null);
  const lastSet = useRef(-1);
  const pb = useTimelinePlayback(ep, lib);
  const want = useRef<number | null>(null); // a playhead move asked for by a page, applied once the episode it changed has been drawn
  const pages = useEditorPages(ep, pb.stop, time => { want.current = time; });
  const { openPage } = pages;

  // Move the playhead (and the lanes under it) to a time.
  const seek = useCallback((time: number) => {
    const x = Math.max(0, Math.min(time, total));
    setT(x);
    const el = scroller.current;
    if (el) { el.scrollLeft = x * PX; lastSet.current = el.scrollLeft; }
  }, [total]);

  // A page that adds a line asks for the playhead to go to its start; the episode (and so the lanes) has grown by then.
  useLayoutEffect(() => {
    if (want.current !== null) { const w = want.current; want.current = null; seek(w); }
    else if (t > total) seek(total);
  }, [ep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Width of the scroller -> padding so time 0 can sit under the playhead.
  useLayoutEffect(() => {
    const el = scroller.current!;
    const ro = new ResizeObserver(() => setHalf(Math.floor(el.clientWidth / 2)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useLayoutEffect(() => { seek(t); }, [half]); // eslint-disable-line react-hooks/exhaustive-deps

  // A user scroll sets the time; one we made ourselves (playback, a page moving the playhead) is ignored. A user scroll stops playback.
  const onScroll = () => {
    const el = scroller.current!;
    if (Math.abs(el.scrollLeft - lastSet.current) < 0.5) return;
    if (pb.playing) pb.stop();
    setT(Math.min(el.scrollLeft / PX, total));
  };

  // Playback drives the scroller.
  useEffect(() => { if (pb.t !== null) seek(pb.t); }, [pb.t]); // eslint-disable-line react-hooks/exhaustive-deps

  // Wheel scrolls sideways.
  useEffect(() => {
    const el = scroller.current!;
    const wheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, []);

  // Mouse drag on the background scrubs; a tap on the background (no drag) deselects.
  const moved = useRef(false);
  const onPointerDown = (e: React.PointerEvent) => {
    moved.current = false;
    if (e.pointerType !== "mouse" || (e.target as HTMLElement).closest(".clip")) return;
    const el = scroller.current!, x0 = e.clientX, s0 = el.scrollLeft;
    const move = (m: PointerEvent) => { if (Math.abs(m.clientX - x0) > 3) moved.current = true; el.scrollLeft = s0 - (m.clientX - x0); };
    const up = () => { removeEventListener("pointermove", move); removeEventListener("pointerup", up); };
    addEventListener("pointermove", move);
    addEventListener("pointerup", up);
  };
  const onBackground = (e: React.MouseEvent) => {
    if (!(e.target as HTMLElement).closest(".clip") && !moved.current) setSel(null);
  };

  // Pictures and face sprites are decoded ahead so playback never shows half a frame.
  const srcKey = imagesInRange(ep, 0, Infinity).join("|");
  useEffect(() => { decodeImages(imagesInRange(ep, 0, Infinity)).catch(() => {}); }, [srcKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlay = () => {
    if (pb.playing) return pb.stop();
    if (!playable) return;
    pb.play(t >= total - 0.1 ? 0 : t);
  };

  // Audio on a picture whose only line is its untouched 1 s silence fills that line instead of adding another.
  const addAt = (kind: PageKind, at: number) => {
    const ph = kind === "line" ? placeholderToFill(ep, at, ep.lines.length > 0 && at >= total - 0.05) : undefined;
    openPage(ph ? { kind, t: ph.start, editId: ph.id, step: "voice" } : { kind, t: at });
  };
  const item = findSel(ep, sel);
  const selNow = item ? sel : null;

  const act = (a: Act) => {
    if (!sel || !item) return;
    const { kind, char, id } = sel;
    if (a === "faces") return openPage({ kind: "picture", t: item.start, editId: id, step: "faces" });
    if (a === "animate") return openPage({ kind: "picture", t: item.start, editId: id, step: "animate" });
    if (a === "edit") return openPage({ kind, t: item.start, char, editId: id });
    if (a === "earlier" || a === "later") {
      const dir = a === "earlier" ? -1 : 1;
      const moved = moveLine(ep, id, dir);
      update(e => moveLine(e, id, dir));
      if (moved !== ep) want.current = lineById(moved, id)?.start ?? null; // the playhead follows the line
    }
    if (a === "delete") {
      if (kind === "line") update(e => removeLine(e, id));
      else update(e => removeMarker(e, kind, id));
      setSel(null);
    }
  };

  const noPicture = !pictureAt(ep, t); // no picture yet at the playhead
  const atEnd = ep.lines.length > 0 && !lineAtTime(ep, t); // at or after the end of the last line: only a picture or a line can go here
  const noMarks = noPicture || atEnd;

  return (
    <>
      <Page className="tl-editor">
        <EditorBar onBack={onBack} playable={playable} onExport={pages.exportIt} />
        <EpisodeHead ep={ep} t={t} playing={pb.playing} playable={playable} onToggle={togglePlay} tiles="timeline" view={view} onView={onView}
          onAddPicture={() => openPage({ kind: "picture", t: ep.pictures.length ? t : 0 })} onFull={() => { pb.stop(); setFull(true); }}>
          <Tile small icon="mic" label="Line" onClick={() => addAt("line", t)} />
          <Tile small icon="image" label="Picture" onClick={() => addAt("picture", t)} />
          <Tile small icon="face" label="Expression" disabled={noMarks} onClick={() => addAt("feeling", t)} />
          <Tile small icon="video" label="Camera" disabled={noMarks} onClick={() => addAt("camera", t)} />
        </EpisodeHead>
        <div className="tl-hint">The buttons add at the playhead</div>
        <div className="tl-v">
          <div className="tl">
            <Heads ep={ep} />
            <div className="tl-wrap">
              <div ref={scroller} className="tl-scroll" onScroll={onScroll} onPointerDown={onPointerDown} onClick={onBackground}>
                <Lanes ep={ep} half={half} sel={selNow} onSel={setSel} onAddAt={(kind, at, char) => openPage({ kind, t: at, char })} />
              </div>
              <div className="tl-playhead"><span>{fmtT(t)}</span></div>
            </div>
          </div>
        </div>
        <Toolbar ep={ep} sel={selNow} onAct={act} onDeselect={() => setSel(null)} />
      </Page>
      {pages.node}
      {full && <FullPlayer ep={ep} from={t} onClose={time => { setFull(false); seek(time); }} />}
    </>
  );
}
